import { ApiError, api } from '@/modules/erp/api/client'
import { setCsrfToken } from '@/modules/erp/api/session'

export type Language = 'et' | 'en'

export interface SessionInfo {
    user: { name: string; login: string }
    company: { id: number; name: string }
    language: Language
    csrf_token: string
}

/**
 * Why single sign-on didn't open the ERP the last time it was tried, for the sign-in page:
 * no_erp_user (no Odoo user has the person's e-mail), sso_off (the company's gateway doesn't
 * offer it), or another code of the gateway's.
 */
let refusal: string | null = null
let lastTried = 0
/** A refusal stands this long: a 401 right after one doesn't ask again at once. */
const RETRY_MS = 10_000

export function ssoRefusal(): string | null {
    return refusal
}

/**
 * The app's sign-in opens the ERP as the Odoo user with the same e-mail (the gateway's
 * POST /api/auth/sso; it answers as /api/auth/me does), or null when it can't.
 */
async function signInThroughApp(): Promise<SessionInfo | null> {
    if (Date.now() - lastTried < RETRY_MS) return null
    lastTried = Date.now()
    try {
        const session = await api.post<SessionInfo>('/api/auth/sso', {}, { handleUnauthorized: false })
        refusal = null
        return session
    } catch (error) {
        refusal = error instanceof ApiError ? (error.code ?? `http_${error.status}`) : 'unreachable'
        return null
    }
}

/**
 * The signed-in session, or null when signed out (a 401 here is an answer, not an error). Signed
 * out, the app's own sign-in is tried first (single sign-on); the ERP's sign-in page only when that
 * can't open it.
 */
export async function fetchSession(): Promise<SessionInfo | null> {
    try {
        const session = await api.get<SessionInfo>('/api/auth/me', { handleUnauthorized: false })
        setCsrfToken(session.csrf_token)
        return session
    } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
            const viaApp = await signInThroughApp()
            setCsrfToken(viaApp?.csrf_token ?? null)
            return viaApp
        }
        throw error
    }
}

/** For tests: forget the last attempt. */
export function resetSso(): void {
    refusal = null
    lastTried = 0
}
